import { useEffect, useState } from 'react';
import type { Apiary, Colony, EventType } from '../../db/model';
import { changesSince, getSetting, read } from '../../db/db';
import { apiaryColonies, naturalLabel, summariesFor } from '../../db/queries';
import { SEASON_SETTING, normStartMonth } from '../../db/season';
import { saveApiary, setDeleted } from '../../db/actions';
import { queenColor, type Queen } from '../../db/model';
import { back, go, useQuery } from '../hooks';
import { colonyName, EVENT_LABEL, QUEEN_COLOR, daysAgo, fmtDate, plural, changedRecords } from '../labels';
import { Empty, Field, Link, Notice, SaveBar, Screen, Section, Sheet, TextInput, toast } from '../ui';
import { saved } from '../confirm';
import { EventIcon, Icon } from '../icons';
import { LicenceNotice } from '../licence';
import { InstallHint } from '../recommend';
import { TasksPanel, useAllTasks } from '../tasks-ui';

// ---------------------------------------------------------------------------
// Úvod: seznam stanovišť
// ---------------------------------------------------------------------------

export function HomeScreen() {
  const data = useQuery(async () => {
    const all = await read.all('apiaries');
    const apiaries = all.filter((a) => !a.archived);
    const colonies = (await read.all('colonies')).filter((c) => c.status === 'active');
    // živá včelstva, jejichž stanoviště chybí (přesun bez cíle ve starší verzi), bylo smazané nebo zrušené –
    // jinak by je uživatel nikde neviděl
    const live = new Set(apiaries.map((a) => a.id));
    const archived = new Map(all.filter((a) => a.archived).map((a) => [a.id, a.name]));
    const homeless = colonies
      .filter((c) => !live.has(c.apiaryId))
      .map((c) => ({ colony: c, reason: archived.has(c.apiaryId) ? `stanoviště ${archived.get(c.apiaryId)} je zrušené` : 'bez stanoviště' }))
      .sort((a, b) => naturalLabel(a.colony, b.colony));
    const lastBackup = await getSetting<string>('lastBackupAt');
    const changes = await changesSince(lastBackup);
    return { apiaries: apiaries.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'cs')), colonies, homeless, lastBackup, changes };
  }, []);
  if (!data) return <Screen title="Včelnice" backTo={false}>{null}</Screen>;
  const { apiaries, colonies, homeless, lastBackup, changes } = data;
  // připomenout zálohu: nikdy nezálohováno, nebo hodně nových zápisů, nebo měsíc bez zálohy (a něco se změnilo)
  const backupOld =
    colonies.length > 0 &&
    (!lastBackup || changes >= 20 || (changes > 0 && Date.now() - Date.parse(lastBackup) > 30 * 86400000));

  return (
    <Screen title="Včelnice" subtitle={`${plural(colonies.length, 'včelstvo', 'včelstva', 'včelstev')} · ${plural(apiaries.length, 'stanoviště', 'stanoviště', 'stanovišť')}`} backTo={false}>
      <InstallHint />
      <LicenceNotice />
      {backupOld && (
        <Notice tone="warn">
          Data jsou jen v tomto telefonu.{' '}
          {lastBackup ? `Poslední záloha ${fmtDate(lastBackup)}, od té doby ${changedRecords(changes)}.` : 'Zatím jste nezálohovali.'}{' '}
          <Link to="nastaveni">Zálohovat</Link>
        </Notice>
      )}
      {apiaries.length === 0 ? (
        <section className="welcome">
          <p className="empty-big welcome-icon" aria-hidden>
            <Icon name="hive" size={60} />
          </p>
          <h2>Vítejte v Úlníku</h2>
          <p className="muted">Všechno se ukládá jen ve vašem telefonu a funguje i bez signálu. Začněte takto:</p>
          <ol className="steps">
            <li>
              <span>
                <strong>Přidejte stanoviště</strong> – místo, kde stojí vaše úly.
              </span>
            </li>
            <li>
              <span>
                <strong>Přidejte včelstva</strong> – každý úl jménem, s matkou a počtem nástavků.
              </span>
            </li>
            <li>
              <span>
                <strong>Vytiskněte QR štítky</strong> na úly – načtením se hned otevře karta včelstva.
              </span>
            </li>
            <li>
              <span>
                <strong>Zapisujte</strong> prohlídky, léčení, krmení a medobraní – přehledy se spočítají samy.
              </span>
            </li>
          </ol>
          <button className="btn primary big wide" onClick={() => go('stanoviste/nove')}>
            + Přidat první stanoviště
          </button>
          <p className="small muted">
            Máte zálohu z jiného telefonu? <Link to="nastaveni">Obnovte ji v Nastavení</Link>.
          </p>
        </section>
      ) : (
        <ul className="cards">
          {apiaries.map((a) => {
            const n = colonies.filter((c) => c.apiaryId === a.id).length;
            return (
              <li key={a.id}>
                <Link to={`stanoviste/${a.id}`} className="card apiary-card">
                  <div className="apiary-name">{a.name}</div>
                  <div className="muted">
                    {plural(n, 'včelstvo', 'včelstva', 'včelstev')}
                    {a.registrationNumber ? ` · reg. č. ${a.registrationNumber}` : ''}
                  </div>
                  <span className="chev">›</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      {homeless.length > 0 && (
        <Section title="Včelstva mimo stanoviště">
          <p className="muted small">
            {homeless.length === 1 ? 'Toto včelstvo nemá' : 'Tato včelstva nemají'} platné stanoviště, a proto nejsou vidět v žádném seznamu.
            Data jsou v pořádku – stačí je přesunout na stanoviště.
          </p>
          <ul className="list homeless-list">
            {homeless.map(({ colony: c, reason }) => (
              <li key={c.id} className="homeless-row">
                <Link to={`vcelstvo/${c.id}`} className="grow">
                  <strong>{colonyName(c.label)}</strong>
                  <span className="small muted"> · {reason}</span>
                </Link>
                {apiaries.length > 0 && (
                  <button className="btn small" onClick={() => go(`zapis/novy?typ=move&vcelstva=${c.id}`)}>
                    Přesunout
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
      {apiaries.length > 0 && (
        <button className="btn big wide" onClick={() => go('stanoviste/nove')}>
          + Přidat stanoviště
        </button>
      )}
      {apiaries.length > 0 && <TasksPanel />}
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Detail stanoviště: karty včelstev + hromadný zásah
// ---------------------------------------------------------------------------

const BULK_TYPES: EventType[] = ['treatment', 'feeding', 'super_change', 'varroa_check', 'inspection', 'harvest', 'note'];

export function ApiaryScreen({ id }: { id: string }) {
  const [showInactive, setShowInactive] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const data = useQuery(async () => {
    const apiary = await read.get('apiaries', id);
    const colonies = await apiaryColonies(id, true);
    const summaries = await summariesFor(colonies.map((c) => c.id), normStartMonth((await getSetting<number>(SEASON_SETTING)) ?? 1));
    const queens = await read.all('queens');
    return { apiary, colonies, summaries, queens: new Map(queens.map((q) => [q.id, q])) };
  }, [id]);
  const tasks = useAllTasks(id);
  if (!data) return null;
  const { apiary, colonies, summaries, queens } = data;
  if (!apiary || apiary.deletedAt) return <Screen title="Stanoviště nenalezeno">{null}</Screen>;
  const active = colonies.filter((c) => c.status === 'active');
  const shown = showInactive ? colonies : active;

  return (
    <Screen
      title={apiary.name}
      crumbs={[['Včelnice', '']]}
      subtitle={apiary.registrationNumber ? `Reg. č. ${apiary.registrationNumber}` : undefined}
      actions={
        <Link to={`stanoviste/${id}/upravit`} className="bar-btn">
          Upravit
        </Link>
      }
    >
      <div className="quick-row">
        <button className="btn" disabled={active.length === 0} onClick={() => setBulkOpen(true)}>
          Zápis pro celé stanoviště
        </button>
        <button className="btn" disabled={active.length === 0} onClick={() => go(`stitky/${id}`)}>
          QR štítky
        </button>
        <button className="btn" disabled={colonies.length === 0} onClick={() => go(`stanoviste/${id}/rok`)}>
          Roční přehled
        </button>
      </div>

      {shown.length === 0 ? (
        <Empty>Na stanovišti zatím nejsou žádná včelstva.</Empty>
      ) : (
        <ul className="colony-grid">
          {shown.map((c) => (
            <li key={c.id}>
              <ColonyCard
                colony={c}
                summary={summaries[c.id]}
                queen={c.currentQueenId ? queens.get(c.currentQueenId) : undefined}
                tone={tasks?.tone.get(c.id)}
              />
            </li>
          ))}
        </ul>
      )}
      <button className="btn big wide" onClick={() => go(`vcelstvo/nove?stanoviste=${id}`)}>
        + Přidat včelstvo
      </button>
      {colonies.length > active.length && (
        <button className="btn link" onClick={() => setShowInactive(!showInactive)}>
          {showInactive ? 'Skrýt' : 'Zobrazit'} neaktivní včelstva ({colonies.length - active.length})
        </button>
      )}

      <Sheet open={bulkOpen} onClose={() => setBulkOpen(false)} title={`Zápis pro ${plural(active.length, 'včelstvo', 'včelstva', 'včelstev')}`}>
        <div className="type-grid">
          {BULK_TYPES.map((t) => (
            <button
              key={t}
              className="type-btn"
              onClick={() => {
                setBulkOpen(false);
                go(`zapis/novy?typ=${t}&stanoviste=${id}&vcelstva=${active.map((c) => c.id).join(',')}`);
              }}
            >
              <span className="type-icon">
                <EventIcon type={t} size={30} />
              </span>
              {EVENT_LABEL[t]}
            </button>
          ))}
        </div>
      </Sheet>
    </Screen>
  );
}

/** delší jména menším písmem, aby se vešla na jeden řádek a dlaždice zůstaly stejně velké */
function labelSize(label: string) {
  const n = label.length;
  return n <= 9 ? '' : n <= 12 ? ' mid' : ' long';
}

function ColonyCard({
  colony,
  summary,
  queen,
  tone,
}: {
  colony: Colony;
  summary?: import('../../db/queries').ColonySummary;
  queen?: Queen;
  tone?: 'ok' | 'warn' | 'danger';
}) {
  const color = queen?.year ? QUEEN_COLOR[queenColor(queen)] : null;
  return (
    <Link
      to={`vcelstvo/${colony.id}`}
      className={'card colony-card' + (colony.status !== 'active' ? ' inactive' : tone ? ` tone-${tone}` : '')}
      ariaLabel={`${colonyName(colony.label)}${tone === 'danger' ? ', vyžaduje pozornost' : tone === 'warn' ? ', něco zkontrolovat' : ''}`}
    >
      <div className="colony-head">
        <span className={'colony-label' + labelSize(colonyName(colony.label))} title={colony.label}>
          {colonyName(colony.label)}
        </span>
        {color && <span className="queen-dot" style={{ background: color.css }} title={`Matka ${queen?.year}`} />}
      </div>
      <div className="colony-meta">
        {colony.status !== 'active' ? (
          <span>neaktivní</span>
        ) : summary?.lastInspection ? (
          <span className="with-icon">
            <Icon name="inspection" size={16} /> {daysAgo(summary.lastInspection)}
          </span>
        ) : (
          <span className="muted">bez prohlídky</span>
        )}
      </div>
      <div className="badges">
        {summary?.queenCells && <span className="badge warn">matečníky</span>}
        {summary?.lastVarroa?.alarm && <span className="badge warn">varroáza</span>}
        {summary?.withdrawalUntil && <span className="badge info">lhůta</span>}
        {!!colony.supers?.length && <span className="badge honey">{colony.supers.length}× medník</span>}
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Formulář stanoviště
// ---------------------------------------------------------------------------

export function ApiaryForm({ id }: { id?: string }) {
  const [form, setForm] = useState<Partial<Apiary> | null>(id ? null : { archived: false });
  useEffect(() => {
    if (id) read.get('apiaries', id).then((a) => setForm(a ?? {}));
  }, [id]);
  if (!form) return null;
  const set = (p: Partial<Apiary>) => setForm({ ...form, ...p });

  const save = async () => {
    if (!form.name?.trim()) return toast('Vyplňte název stanoviště.');
    const rec = await saveApiary({ ...(form as Apiary), name: form.name.trim(), archived: form.archived ?? false });
    saved();
    if (id) back(`stanoviste/${id}`);
    else go(`stanoviste/${rec.id}`, true);
  };

  const locate = () => {
    if (!navigator.geolocation) return toast('Poloha není dostupná.');
    navigator.geolocation.getCurrentPosition(
      (p) => set({ location: { lat: +p.coords.latitude.toFixed(6), lng: +p.coords.longitude.toFixed(6) } }),
      () => toast('Polohu se nepodařilo zjistit.'),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  return (
    <Screen title={id ? 'Upravit stanoviště' : 'Nové stanoviště'} backTo={id ? `stanoviste/${id}` : ''}>
      <Field label="Název" htmlFor="a-name">
        <TextInput id="a-name" value={form.name} onChange={(v) => set({ name: v })} placeholder="např. Za chalupou" autoFocus={!id} />
      </Field>
      <Field label="Registrační číslo stanoviště" htmlFor="a-reg" hint="Z ústřední evidence (ČMSCH), nepovinné">
        <TextInput id="a-reg" value={form.registrationNumber} onChange={(v) => set({ registrationNumber: v })} />
      </Field>
      <Field label="Katastrální území / obec" htmlFor="a-kat">
        <TextInput id="a-kat" value={form.cadastralArea} onChange={(v) => set({ cadastralArea: v })} />
      </Field>
      <Field label="Poloha">
        <div className="row">
          <button type="button" className="btn" onClick={locate}>
            <Icon name="pin" size={20} /> Použít aktuální polohu
          </button>
          {form.location && (
            <a className="muted" target="_blank" rel="noreferrer" href={`https://mapy.cz/zakladni?q=${form.location.lat},${form.location.lng}`}>
              {form.location.lat}, {form.location.lng}
            </a>
          )}
        </div>
      </Field>
      <Field label="Poznámka" htmlFor="a-note">
        <TextInput id="a-note" multiline value={form.note} onChange={(v) => set({ note: v })} />
      </Field>
      {id && (
        <Section>
          <label className="check">
            <input type="checkbox" checked={!!form.archived} onChange={(e) => set({ archived: e.target.checked })} />
            Stanoviště je zrušené (skrýt v přehledu)
          </label>
          <button
            className="btn danger"
            onClick={async () => {
              const inside = (await read.byIndex('colonies', 'apiaryId', id)).filter((c) => c.status === 'active');
              if (inside.length)
                return toast(
                  `Na stanovišti ${inside.length === 1 ? 'je 1 včelstvo' : inside.length < 5 ? `jsou ${inside.length} včelstva` : `je ${inside.length} včelstev`}. Nejdřív je přesuňte jinam, nebo stanoviště jen označte jako zrušené.`,
                );
              await setDeleted('apiaries', id, true);
              saved('Stanoviště smazáno', { label: 'Vrátit', run: () => setDeleted('apiaries', id, false) });
              go('', true);
            }}
          >
            Smazat stanoviště
          </button>
        </Section>
      )}
      <SaveBar onSave={save} />
    </Screen>
  );
}
